import React, { useState, useEffect } from "react";
import { Box, Typography, Dialog, DialogTitle, DialogContent, IconButton, CircularProgress } from "@mui/material";
import { X } from "lucide-react";
import axiosInstance from "../../utils/axiosInstance";

const ChatThreadDialog = ({
  threadId,
  open,
  onClose,
  initialThread = null,
}) => {
  const [selectedThread, setSelectedThread] = useState(initialThread);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (open && threadId) {
      fetchThreadDetail(threadId);
    } else {
      setSelectedThread(null);
    }
  }, [open, threadId]);

  const fetchThreadDetail = async (id) => {
    setLoadingDetail(true);
    // Optimistically open with metadata if available
    setSelectedThread(prev => prev || { chat_thread_id: id });
    try {
      const response = await axiosInstance.get(`/analytics/assistant-chats/${id}`);
      if (response.data.success) {
        setSelectedThread(prev => ({ ...prev, ...response.data.data }));
      }
    } catch (error) {
      console.error("Failed to fetch thread details", error);
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Chat Thread</span>
        <IconButton onClick={onClose} size="small">
          <X className="w-5 h-5" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: '#FAFAFA' }}>
        {(() => {
          if (!selectedThread?.product_url) return null;
          let info = null;
          try {
            info = typeof selectedThread.product_info === 'string'
              ? JSON.parse(selectedThread.product_info)
              : selectedThread.product_info;
          } catch (e) { }

          return (
            <Box sx={{ mb: 3, p: 2, bgcolor: "#FFF", borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)", border: "1px solid rgba(0,0,0,0.08)" }}>
              <Typography sx={{ fontSize: "12px", color: "#666", mb: 1.5, fontWeight: 600, textTransform: "uppercase" }}>
                Selected Product Context
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, textAlign: "left" }}>
                {info?.image_url && (
                  <Box component="img" src={info.image_url} sx={{ width: 60, height: 80, borderRadius: "6px", objectFit: "cover", border: "1px solid #eee" }} />
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "13px", color: "#333", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", mb: 1, fontWeight: 500 }}>
                    {info?.title || "Unknown Product"}
                  </Typography>
                  <Typography sx={{ fontSize: "12px" }}>
                    <a href={selectedThread.product_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium">
                      View Product Page &rarr;
                    </a>
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })()}
        {loadingDetail && !selectedThread?.messages && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {selectedThread && selectedThread.messages && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {selectedThread.messages.map((message, i) => (
              <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* User Message */}
                {message.role === 'user' && (
                  <Box sx={{ alignSelf: 'flex-end', maxWidth: '85%' }}>
                    <Box sx={{
                      px: 2, py: 1.25, borderRadius: '16px 16px 4px 16px',
                      background: 'linear-gradient(135deg, #FF8C38 0%, #FF9B80 100%)',
                      color: '#FFF',
                      boxShadow: '0 4px 12px rgba(255, 140, 56, 0.2)',
                      mb: 0.5
                    }}>
                      <Typography sx={{ fontSize: '13.5px', whiteSpace: 'pre-wrap' }}>{message.text}</Typography>
                    </Box>
                  </Box>
                )}
                {/* AI Response */}
                {message.role === 'assistant' && (
                  <Box sx={{ alignSelf: 'flex-start', maxWidth: '85%' }}>
                    <Box sx={{
                      px: 2, py: 1.25, borderRadius: '16px 16px 16px 4px',
                      backgroundColor: '#FFF',
                      color: '#1F1F1F',
                      border: '1px solid rgba(0,0,0,0.04)',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                      mb: 0.5
                    }}>
                      <Typography sx={{ fontSize: '13.5px', whiteSpace: 'pre-wrap' }}>{message.text}</Typography>
                    </Box>
                    {message.search_query && (() => {
                      const sq = message.search_query;
                      let sqDetails = sq.keyword || 'None';
                      const extras = [];
                      if (sq.gender) extras.push(`Gender: ${sq.gender}`);
                      if (sq.color) extras.push(`Color: ${sq.color}`);
                      if (sq.min_price != null) extras.push(`Min Price: Rs.${sq.min_price}`);
                      if (sq.max_price != null) extras.push(`Max Price: Rs.${sq.max_price}`);
                      if (extras.length > 0) sqDetails += ` | ${extras.join(', ')}`;

                      return (
                        <Typography sx={{ fontSize: '11px', color: '#0ea5e9', ml: 1 }}>
                          [AI Searched: {sqDetails}]
                        </Typography>
                      );
                    })()}
                    {message.products && message.products.length > 0 && (
                      <>
                        <Typography sx={{ fontSize: '11px', color: '#22c55e', ml: 1, mb: 1 }}>
                          [{message.products.length} products returned]
                        </Typography>
                        {/* Horizontal Product Cards */}
                        <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1, ml: 1 }}>
                          {message.products.map((p, pIdx) => (
                            <Box key={pIdx} sx={{
                              minWidth: 120, width: 120,
                              bgcolor: '#fff',
                              border: '1px solid #eee',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              display: 'flex',
                              flexDirection: 'column'
                            }}>
                              <Box component="img" src={p.image_url || p.images?.[0]} sx={{ width: '100%', height: 100, objectFit: 'cover', bgcolor: '#f9f9f9' }} />
                              <Box sx={{ p: 1 }}>
                                <Typography sx={{ fontSize: '10px', color: '#666', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
                                  {p.brand || "Brand"}
                                </Typography>
                                <Typography sx={{ fontSize: '11px', color: '#333', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.2 }}>
                                  {p.title}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChatThreadDialog;
